import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Users, HeartHandshake, ShieldCheck, Sparkles, Mail, MessageCircle, Search, UserCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const PageShell = ({ title, subtitle, icon: Icon, children, testid }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-kora-sand" data-testid={testid}>
      <header className="bg-kora-ink text-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <button data-testid="static-back" onClick={() => navigate("/")} className="text-white/90 hover:text-white">
            <ArrowLeft size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-kora-terracotta flex items-center justify-center">
              <Heart size={14} className="text-white" fill="white" />
            </div>
            <span className="font-display font-bold text-lg">KoraLove</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          {Icon && (
            <div className="w-11 h-11 rounded-2xl bg-kora-terracotta/10 text-kora-terracotta flex items-center justify-center">
              <Icon size={22} />
            </div>
          )}
          <h1 className="font-display font-black text-3xl sm:text-4xl text-kora-ink">{title}</h1>
        </div>
        {subtitle && <p className="text-kora-ink/60 mb-8 ml-0.5">{subtitle}</p>}
        <div className="prose-kora space-y-5 text-kora-ink/80 leading-relaxed">{children}</div>

        <div className="mt-12 pt-6 border-t border-kora-line">
          <button onClick={() => navigate("/")} className="text-sm font-bold text-kora-terracotta hover:text-kora-ochre transition-colors">
            ← Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
};

const H2 = ({ children }) => <h2 className="font-display font-bold text-xl text-kora-ink mt-6 mb-1">{children}</h2>;

const PlanCard = ({ name, price, badge, items, highlight }) => (
  <div className={`not-prose bg-white rounded-2xl border p-5 relative ${highlight ? "border-kora-terracotta shadow-[0_6px_20px_rgba(224,93,54,0.15)]" : "border-kora-line"}`}>
    {badge && (
      <span className="absolute -top-2.5 left-5 bg-kora-ochre text-kora-ink px-3 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide">{badge}</span>
    )}
    <div className="flex items-baseline justify-between">
      <p className="font-display font-bold text-lg text-kora-ink">{name}</p>
      <p className="font-display font-black text-2xl text-kora-ink">{price}<span className="text-xs text-kora-muted font-medium">{price !== "0 FCFA" ? " / 30 jours" : ""}</span></p>
    </div>
    <ul className="mt-3 space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="text-sm text-kora-ink/80 flex items-start gap-2">
          <span className="text-kora-terracotta mt-0.5">✓</span> {it}
        </li>
      ))}
    </ul>
  </div>
);

export const Tarifs = () => (
  <PageShell title="Tarifs" subtitle="Des formules simples et transparentes, sans engagement." icon={ShieldCheck} testid="page-tarifs">
    <div className="space-y-6 mt-2">
      <PlanCard
        name="Gratuit"
        price="0 FCFA"
        items={[
          "Création de profil et photos",
          "Découverte de profils et likes illimités",
          "3 messages gratuits par 24h",
          "Messages illimités avec les membres Premium",
          "Premier message (brise-glace) après un like",
        ]}
      />
      <PlanCard
        name="KoraLove Premium"
        price="9 $"
        badge="Le plus populaire"
        highlight
        items={[
          "Messages illimités avec tous vos matchs",
          "Voyez qui vous a liké(e) et qui a visité votre profil",
          "Appels vidéo avec vos matchs",
          "Super Likes pour vous démarquer",
          "Filtrage avancée par Corpulence et par Taille",
          "Mode invisible et badge Premium",
        ]}
      />
      <p className="text-xs text-kora-muted">Pass de 30 jours à paiement unique (9 $, payable en Mobile Money ou carte bancaire dans votre devise locale), sans abonnement ni renouvellement automatique. Voir aussi notre Politique de remboursement.</p>
    </div>
  </PageShell>
);

export const DemandesLegales = () => (
  <PageShell title="Demandes légales" subtitle="Informations à l'attention des autorités judiciaires et forces de l'ordre." icon={ShieldCheck} testid="page-demandes-legales">
    <p>KoraLove s'engage à protéger la vie privée de ses utilisateurs. Nous ne divulguons des informations relatives à un compte que dans le cadre strict prévu par la loi, sur présentation d'une demande légale valide émanant d'une autorité compétente.</p>
    <p>Cette page ne s'adresse pas à nos utilisateurs mais aux services de police, gendarmerie, magistrats et autorités judiciaires souhaitant obtenir des informations dans le cadre d'une enquête officielle.</p>
    <H2>Qui peut soumettre une demande</H2>
    <p>Les demandes doivent émaner d'une autorité légalement habilitée : services de police ou de gendarmerie, procureur, juge d'instruction, ou toute autorité judiciaire compétente, agissant dans le cadre d'une procédure légale (réquisition judiciaire, commission rogatoire, mandat, ordonnance de justice, etc.).</p>
    <p>Nous ne traitons pas les demandes provenant de particuliers, d'avocats agissant seuls sans mandat judiciaire, ou de toute personne non habilitée par la loi.</p>
    <H2>Comment nous contacter</H2>
    <p>Toute demande doit être adressée par écrit, sur papier à en-tête officiel, à l'adresse suivante :</p>
    <p><strong>Email dédié :</strong> <a href="mailto:contact@koralove.org" className="text-kora-terracotta font-semibold">contact@koralove.org</a></p>
    <p>Merci de joindre :</p>
    <ul className="list-disc pl-5 space-y-1">
      <li>La référence de la procédure (numéro de dossier, référence du parquet, etc.)</li>
      <li>L'identité et la fonction du signataire habilité</li>
      <li>Le fondement légal de la demande (article de loi, mandat, etc.)</li>
      <li>Les informations précises recherchées (identifiant de compte, adresse email, pseudonyme, période concernée)</li>
      <li>Les coordonnées de contact du service requérant, pour vérification</li>
    </ul>
    <H2>Types d'informations pouvant être communiquées</H2>
    <p>Selon la nature et la portée de la demande, et dans les limites autorisées par la loi, nous pouvons être en mesure de fournir :</p>
    <ul className="list-disc pl-5 space-y-1">
      <li>Informations d'inscription associées à un compte (email, date de création)</li>
      <li>Historique de connexion (adresses IP, horodatage)</li>
      <li>Contenu de messagerie, sur mandat spécifique le prévoyant expressément</li>
    </ul>
    <p>KoraLove se réserve le droit d'évaluer chaque demande au cas par cas et peut la refuser si elle est incomplète, non conforme, ou insuffisamment fondée juridiquement.</p>
    <H2>Délais de traitement</H2>
    <p>Nous accusons réception de toute demande légale valide sous 5 jours ouvrés. Le délai de traitement complet dépend de la nature et de la complexité de la demande, généralement entre 2 et 4 semaines.</p>
    <H2>Demandes urgentes</H2>
    <p>En cas de danger imminent pour la vie ou la sécurité d'une personne, une demande peut être signalée comme urgente à l'adresse <a href="mailto:contact@koralove.org" className="text-kora-terracotta font-semibold">contact@koralove.org</a>, avec le fondement de l'urgence clairement indiqué. Ces demandes restent soumises aux mêmes exigences de justification légale.</p>
    <H2>Conservation des données</H2>
    <p>KoraLove conserve les données utilisateurs conformément à sa politique de confidentialité et aux obligations légales applicables. La suppression d'un compte par un utilisateur n'entraîne pas nécessairement la suppression immédiate de toutes les données associées, certaines pouvant être conservées pour répondre à des obligations légales.</p>
    <H2>Notification des utilisateurs</H2>
    <p>Sauf interdiction légale expresse (par exemple en cas de non-divulgation ordonnée par un tribunal), KoraLove peut informer l'utilisateur concerné qu'une demande légale a été reçue à son sujet.</p>
    <p>Pour toute question relative à cette page, contactez <a href="mailto:contact@koralove.org" className="text-kora-terracotta font-semibold">contact@koralove.org</a>.</p>
  </PageShell>
);

export const Coordonnees = () => null;

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-2xl border border-kora-line p-5 flex gap-4">
    <div className="w-10 h-10 shrink-0 rounded-full bg-kora-terracotta/10 text-kora-terracotta flex items-center justify-center">
      <Icon size={18} />
    </div>
    <div>
      <p className="font-bold text-kora-ink mb-1">{title}</p>
      <p className="text-sm text-kora-ink/70 leading-relaxed">{children}</p>
    </div>
  </div>
);

export const APropos = () => (
  <PageShell title="À propos" subtitle="Notre mission : rapprocher les cœurs de la diaspora africaine." icon={Users} testid="page-a-propos">
    <p>
      KoraLove est né d'un constat simple : loin du pays, il est parfois difficile de rencontrer des personnes qui
      partagent la même culture, les mêmes valeurs et les mêmes envies. Nous avons créé une plateforme chaleureuse et
      bienveillante, pensée pour la diaspora africaine en Europe, aux États-Unis, en Chine et au Canada.
    </p>
    <H2>Deux intentions, une même simplicité</H2>
    <p>
      Que vous cherchiez une <strong>belle amitié</strong> ou une <strong>relation sérieuse en vue du mariage</strong>,
      vous choisissez votre intention dès le départ. Vous rencontrez ainsi des personnes qui recherchent la même chose que vous.
    </p>
    <H2>Nos valeurs</H2>
    <div className="grid sm:grid-cols-2 gap-3 not-prose">
      <Card icon={HeartHandshake} title="Respect">Une communauté bienveillante où chacun se sent en confiance.</Card>
      <Card icon={ShieldCheck} title="Sécurité">Profils vérifiés et outils de signalement pour des échanges sereins.</Card>
      <Card icon={Users} title="Proximité culturelle">Filtrez par pays d'origine et de résidence pour trouver la bonne personne.</Card>
      <Card icon={Heart} title="Sincérité">Des rencontres authentiques, pour l'amitié comme pour le mariage.</Card>
    </div>
  </PageShell>
);

export const CommentCaMarche = () => (
  <PageShell title="Comment ça marche" subtitle="Trouvez la bonne personne en quelques étapes simples." icon={Sparkles} testid="page-comment-ca-marche">
    <div className="space-y-3 not-prose">
      <Card icon={UserCheck} title="1. Créez votre profil">
        Inscrivez-vous en quelques secondes, ajoutez vos plus belles photos et indiquez votre intention : amitié ou mariage.
      </Card>
      <Card icon={Search} title="2. Découvrez des profils">
        Parcourez des profils de la diaspora et filtrez selon vos préférences (âge, ville, pays d'origine et de résidence…).
      </Card>
      <Card icon={Heart} title="3. Aimez et matchez">
        Aimez les profils qui vous plaisent. Lorsqu'un intérêt est réciproque, c'est un match : vous pouvez discuter !
      </Card>
      <Card icon={MessageCircle} title="4. Discutez et rencontrez">
        Échangez par messages, faites connaissance à votre rythme, puis organisez une rencontre en toute simplicité.
      </Card>
    </div>
    <H2>Et le Premium ?</H2>
    <p>
      L'inscription et les fonctionnalités essentielles sont gratuites. L'abonnement <strong>Premium</strong> débloque
      les messages illimités, les appels vidéo, la liste de vos visiteurs et bien plus, pour maximiser vos chances de rencontre.
    </p>
  </PageShell>
);

export const ConseilsSecurite = () => (
  <PageShell title="Conseils sécurité" subtitle="Votre sécurité est notre priorité. Quelques bons réflexes." icon={ShieldCheck} testid="page-conseils-securite">
    <H2>Protégez vos informations personnelles</H2>
    <p>Ne communiquez jamais vos coordonnées bancaires, mots de passe ou documents d'identité. Un profil sérieux ne vous demandera jamais d'argent.</p>
    <H2>Méfiez-vous des demandes d'argent</H2>
    <p>Soyez très vigilant si une personne, même après plusieurs échanges, vous demande de l'argent ou une aide financière, quel qu'en soit le motif (urgence, voyage, douane…). C'est le signal d'arnaque le plus courant.</p>
    <H2>Prenez votre temps</H2>
    <p>Apprenez à connaître la personne via la messagerie et les appels vidéo avant toute rencontre. Faites confiance à votre intuition.</p>
    <H2>Pour un premier rendez-vous</H2>
    <ul className="list-disc pl-5 space-y-1">
      <li>Choisissez un lieu public et fréquenté.</li>
      <li>Prévenez un proche de l'endroit et de l'heure.</li>
      <li>Organisez votre propre transport aller-retour.</li>
    </ul>
    <H2>Signalez et bloquez</H2>
    <p>
      En cas de comportement suspect, déplacé ou de faux profil, utilisez les boutons <strong>Signaler</strong> et
      <strong> Bloquer</strong> disponibles sur chaque profil et dans la messagerie. Notre équipe examine chaque signalement.
    </p>
  </PageShell>
);

export const Conditions = () => (
  <PageShell title="Conditions d'utilisation" subtitle="Dernière mise à jour : juin 2026." icon={ShieldCheck} testid="page-conditions">
    <H2>1. Acceptation des conditions</H2>
    <p>En créant un compte et en utilisant KoraLove, vous acceptez les présentes conditions d'utilisation. Si vous n'êtes pas d'accord, veuillez ne pas utiliser le service.</p>
    <H2>2. Admissibilité</H2>
    <p>Vous devez être âgé d'au moins 18 ans pour utiliser KoraLove. Vous vous engagez à fournir des informations exactes et à maintenir votre profil à jour.</p>
    <H2>3. Comportement des membres</H2>
    <p>Il est interdit de publier des contenus illégaux, haineux, à caractère sexuel explicite, ou de harceler d'autres membres. Tout manquement peut entraîner la suspension ou la suppression du compte.</p>
    <H2>4. Abonnement Premium</H2>
    <p>Les fonctionnalités Premium sont proposées via un abonnement payant. Les conditions de tarification et de renouvellement sont indiquées au moment de la souscription.</p>
    <H2>5. Responsabilité</H2>
    <p>KoraLove met tout en œuvre pour offrir un environnement sûr mais ne peut garantir le comportement des membres. Les rencontres se font sous votre responsabilité.</p>
    <H2>6. Modifications</H2>
    <p>Nous pouvons mettre à jour ces conditions à tout moment. Les changements importants vous seront notifiés dans l'application.</p>
    <Coordonnees />
  </PageShell>
);

export const PUA = () => (
  <PageShell title="Politique d'utilisation acceptable (PUA)" subtitle="Les règles pour une communauté sûre et respectueuse." icon={ShieldCheck} testid="page-pua">
    <H2>1. Objet</H2>
    <p>La présente Politique d'utilisation acceptable définit les comportements autorisés et interdits sur KoraLove. Elle complète nos Conditions d'utilisation et s'applique à tous les membres.</p>
    <H2>2. Comportements attendus</H2>
    <p>Respectez les autres membres, soyez honnête sur votre identité et vos intentions (rencontre amicale ou mariage), et utilisez vos propres photos récentes.</p>
    <H2>3. Contenus et comportements interdits</H2>
    <p>Sont strictement interdits : le harcèlement, les menaces et les discours haineux ; les contenus à caractère sexuel explicite ; l'usurpation d'identité et les faux profils ; les escroqueries, demandes d'argent et arnaques sentimentales ; la sollicitation commerciale, la publicité et le spam ; le partage des données personnelles d'autrui sans consentement ; toute activité illégale.</p>
    <H2>4. Protection des mineurs</H2>
    <p>KoraLove est strictement réservé aux personnes âgées de 18 ans et plus. Tout compte suspecté d'appartenir à un mineur sera immédiatement supprimé.</p>
    <H2>5. Signalement</H2>
    <p>Chaque profil peut être signalé directement dans l'application. Notre équipe examine tous les signalements et prend les mesures appropriées. Vous pouvez aussi nous contacter via la page Contact.</p>
    <H2>6. Sanctions</H2>
    <p>Tout manquement à cette politique peut entraîner un avertissement, une suspension temporaire ou la suppression définitive du compte, sans remboursement des abonnements en cours en cas de violation grave.</p>
    <Coordonnees />
  </PageShell>
);

export const Remboursement = () => (
  <PageShell title="Politique de remboursement" subtitle="Conditions applicables aux abonnements KoraLove Premium." icon={ShieldCheck} testid="page-remboursement">
    <H2>1. Achats concernés</H2>
    <p>Cette politique s'applique au pass KoraLove Premium (pass de 30 jours à paiement unique), réglé via notre prestataire de paiement sécurisé (Mobile Money et carte bancaire).</p>
    <H2>2. Principe général</H2>
    <p>Les paiements d'abonnement sont dus pour la période en cours et ne sont en principe pas remboursables, la fourniture du service commençant immédiatement après la souscription.</p>
    <H2>3. Cas de remboursement</H2>
    <p>Un remboursement peut être accordé dans les cas suivants : double facturation ou erreur de paiement avérée ; défaillance technique majeure de notre fait empêchant l'utilisation du service Premium pendant une période prolongée ; toute situation où la loi applicable l'exige.</p>
    <H2>4. Fin du pass</H2>
    <p>Le pass Premium est valable 30 jours et n'est pas renouvelé automatiquement : aucun prélèvement récurrent n'est effectué. Vous conservez vos avantages Premium jusqu'à la date d'expiration du pass, puis vous pouvez le renouveler librement.</p>
    <H2>5. Comment demander un remboursement</H2>
    <p>Adressez votre demande via la page Contact en précisant l'email de votre compte et la date du paiement concerné. Nous répondons sous 5 jours ouvrés. Les remboursements approuvés sont recrédités sur le moyen de paiement d'origine sous 5 à 10 jours ouvrés.</p>
    <Coordonnees />
  </PageShell>
);

export const Confidentialite = () => (
  <PageShell
    title="Politique de confidentialité"
    subtitle="KoraLove — TubeOptimizer LLC · En vigueur le 28 septembre 2026 · Dernière mise à jour le 28 septembre 2026"
    icon={ShieldCheck}
    testid="page-confidentialite"
  >
    <p>La présente politique de confidentialité régit la manière dont l'application mobile KoraLove (Android et iOS) et le service KoraLove (<a href="https://koralove.org" className="text-kora-terracotta underline">https://koralove.org</a>) collectent, utilisent, partagent et protègent vos données personnelles. En créant un compte ou en utilisant l'application, vous acceptez les pratiques décrites dans ce document.</p>

    <H2>1. Identification et portée</H2>
    <p><strong>Application :</strong> KoraLove — application de rencontres sérieuses (amitié et mariage) pour la diaspora africaine.</p>
    <p><strong>Éditeur et responsable du traitement :</strong> TubeOptimizer LLC, 30 N Gould St, STE R, Sheridan, WY 82801, États-Unis.</p>
    <p><strong>Date d'entrée en vigueur :</strong> 28 septembre 2026. <strong>Dernière mise à jour :</strong> 28 septembre 2026.</p>
    <p>Ce document s'applique à l'application mobile KoraLove publiée sur Google Play et l'App Store ainsi qu'au site web koralove.org, qui partagent le même compte et les mêmes données. Nous nous engageons à traiter vos données de façon transparente, sécurisée et conforme au RGPD (UE), au CCPA (Californie) et aux lois locales applicables.</p>

    <H2>2. Données collectées</H2>
    <p>Nous collectons uniquement les données nécessaires au fonctionnement du service de rencontre.</p>
    <p><strong>2.1 Données que vous nous fournissez directement</strong></p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li><strong>Identifiants de compte :</strong> nom, adresse e-mail et photo issus de votre compte Google lors de la connexion (Google Sign-In).</li>
      <li><strong>Informations de profil :</strong> prénom, genre, personnes recherchées, date de naissance (âge), objectif (amitié ou mariage), pays d'origine et de résidence, langue, situation matrimoniale, enfants, niveau d'étude, silhouette, taille, consommation d'alcool et de tabac, biographie, centres d'intérêt et qualités recherchées chez un partenaire.</li>
      <li><strong>Photos de profil</strong> que vous téléchargez (1 à 5 photos, visage visible).</li>
      <li><strong>Contenu des messages</strong> échangés avec vos matchs, ainsi que les appels vidéo (le flux audio/vidéo transite de pair à pair et n'est jamais enregistré par KoraLove).</li>
      <li><strong>Signalements et blocages</strong> effectués envers d'autres membres (motif du signalement).</li>
      <li><strong>Pays et numéro de téléphone</strong> si vous souscrivez à l'abonnement Premium via Chariow sur le site web.</li>
      <li><strong>Motif de suppression de compte</strong> si vous en indiquez un.</li>
    </ul>
    <p><strong>2.2 Données collectées automatiquement</strong></p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li><strong>Données de session :</strong> jeton de session sécurisé, dates et heures de connexion, statut de présence en ligne (dernière activité).</li>
      <li><strong>Données techniques :</strong> adresse IP, type et modèle d'appareil, système d'exploitation et sa version, langue de l'appareil, version de l'application.</li>
      <li><strong>Jeton de notification push</strong> (Expo Push Token) si vous activez les notifications.</li>
      <li><strong>Activité dans l'application :</strong> likes, super likes, passes, matchs, profils visités et visiteurs de votre profil, compteurs d'utilisation (quotas gratuits).</li>
      <li><strong>Journaux techniques du serveur</strong> (requêtes, erreurs) nécessaires à la sécurité et au bon fonctionnement.</li>
    </ul>
    <p>Nous ne collectons <strong>PAS</strong> d'identifiant publicitaire (Google Advertising ID / IDFA) et n'utilisons aucun SDK publicitaire ni d'outil d'analyse d'audience tiers.</p>
    <p><strong>2.3 Autorisations de l'appareil</strong></p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li><strong>Caméra et microphone :</strong> uniquement pour prendre vos photos de profil et passer des appels vidéo avec vos matchs (fonctionnalité Premium), à votre demande.</li>
      <li><strong>Photos / stockage :</strong> uniquement pour choisir les photos que vous ajoutez à votre profil.</li>
      <li><strong>Notifications :</strong> pour vous prévenir des nouveaux messages, likes, matchs et visites.</li>
    </ul>
    <p>Nous ne demandons <strong>PAS</strong> l'accès à votre localisation, à vos contacts ni à vos fichiers autres que les photos choisies. Chaque autorisation peut être refusée ou révoquée dans les réglages de votre téléphone ; l'application reste utilisable avec des fonctionnalités réduites.</p>

    <H2>3. Finalités du traitement</H2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Fournir et maintenir les fonctionnalités principales : création du profil, découverte de profils, likes, matchs, messagerie, appels vidéo, sélection du jour, visiteurs du profil.</li>
      <li>Gérer votre compte et votre authentification (connexion Google, session sécurisée, vérification manuelle du profil par notre équipe de modération).</li>
      <li>Gérer l'abonnement Premium et vérifier son statut (achats intégrés via RevenueCat / Google Play / Apple, ou Chariow sur le site web).</li>
      <li>Vous envoyer des notifications liées à votre activité (messages, likes, matchs, visites) et des e-mails de service (confirmation d'inscription, décision de modération).</li>
      <li>Assurer la sécurité du service, prévenir les fraudes, les faux profils et les comportements abusifs (modération, signalements, blocages, limitation des quotas).</li>
      <li>Respecter nos obligations légales et répondre aux demandes des autorités compétentes.</li>
    </ul>
    <p>Nous n'utilisons <strong>PAS</strong> vos données à des fins publicitaires, de profilage marketing ou d'analyse d'audience par des tiers.</p>

    <H2>4. Partage des données et services tiers</H2>
    <p>Nous ne vendons ni ne louons jamais vos données personnelles à des tiers.</p>
    <p>Vos données de profil public (prénom, âge, photos, pays, biographie, centres d'intérêt, statut en ligne, badge vérifié) sont visibles par les autres membres de KoraLove conformément au fonctionnement du service. Les membres gratuits voient certaines informations floutées.</p>
    <p>Nous partageons des données uniquement avec les prestataires suivants, dans la stricte mesure nécessaire :</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li><strong>Hébergement et API KoraLove</strong> (koralove.org) : stockage sécurisé des comptes, profils, photos et messages.</li>
      <li><strong>Google Sign-In</strong> (Google LLC) : authentification de votre compte — <a href="https://policies.google.com/privacy" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">https://policies.google.com/privacy</a></li>
      <li><strong>RevenueCat, Inc.</strong> : gestion des abonnements intégrés (identifiant utilisateur anonyme, statut d'abonnement) — <a href="https://www.revenuecat.com/privacy" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">https://www.revenuecat.com/privacy</a></li>
      <li><strong>Google Play Billing et Apple In-App Purchase</strong> : traitement des paiements dans l'application ; KoraLove n'accède jamais à vos données bancaires.</li>
      <li><strong>Chariow</strong> : traitement des paiements Premium effectués sur le site web (pays, numéro de téléphone, moyen de paiement) — <a href="https://chariow.com" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">https://chariow.com</a></li>
      <li><strong>Expo (Expo, Inc.)</strong> : acheminement des notifications push vers votre appareil (jeton de notification) — <a href="https://expo.dev/privacy" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">https://expo.dev/privacy</a></li>
      <li><strong>Serveurs STUN de Google</strong> : établissement des appels vidéo WebRTC (adresse IP uniquement, aucun contenu).</li>
    </ul>
    <p>Nous pouvons également divulguer des données si la loi l'exige, pour protéger les droits, la sécurité des membres ou de KoraLove, ou dans le cadre d'une réorganisation de l'entreprise (les utilisateurs en seraient informés).</p>

    <H2>5. Sécurité et conservation des données</H2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li><strong>Chiffrement en transit :</strong> toutes les communications entre l'application et nos serveurs utilisent HTTPS/TLS ; les appels vidéo sont chiffrés (DTLS-SRTP).</li>
      <li><strong>Chiffrement au repos :</strong> les données sont stockées sur des serveurs sécurisés à accès restreint ; le jeton de session est conservé sur votre téléphone dans le stockage chiffré du système (Keychain iOS / Keystore Android).</li>
      <li><strong>Aucun mot de passe n'est stocké par KoraLove :</strong> l'authentification est déléguée à Google.</li>
      <li><strong>Durée de conservation :</strong> vos données sont conservées tant que votre compte est actif. Après suppression du compte, les données personnelles (profil, photos, messages, matchs) sont effacées de nos systèmes actifs sous 30 jours et des sauvegardes sous 90 jours maximum.</li>
      <li>Les données nécessaires à la prévention des fraudes (signalements, comptes bannis) et les justificatifs de facturation peuvent être conservés pour la durée légale applicable, sous forme minimisée.</li>
      <li>Les journaux techniques sont conservés au maximum 12 mois.</li>
    </ul>

    <H2>6. Suppression des données et vos droits</H2>
    <p>Vous pouvez supprimer votre compte et l'ensemble des données associées à tout moment, directement dans l'application :</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Onglet <strong>Profil → Paramètres → Gestion du compte → « Supprimer mon compte »</strong>, puis confirmez. La suppression est définitive.</li>
      <li>Vous pouvez aussi <strong>mettre votre compte en pause</strong> (profil invisible) depuis le même écran.</li>
      <li>Vous pouvez également demander la suppression par e-mail à <strong>contact@tubeoptimizer.net</strong> depuis l'adresse liée à votre compte ; nous traitons la demande sous 30 jours.</li>
      <li>Vous pouvez modifier vos informations et photos à tout moment depuis <strong>Profil → Modifier le profil</strong>.</li>
      <li>Vous pouvez retirer l'accès de KoraLove à votre compte Google depuis <a href="https://myaccount.google.com/permissions" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">https://myaccount.google.com/permissions</a>.</li>
    </ul>
    <p>Conformément au RGPD et aux lois applicables, vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données, ainsi que du droit de retirer votre consentement et d'introduire une réclamation auprès de l'autorité de protection des données de votre pays. Pour exercer ces droits, écrivez-nous à <strong>contact@tubeoptimizer.net</strong>.</p>

    <H2>7. Protection des mineurs</H2>
    <p>KoraLove est strictement réservée aux personnes âgées de <strong>18 ans et plus</strong>. L'âge est demandé à l'inscription et chaque profil est vérifié manuellement par notre équipe avant d'être approuvé.</p>
    <p>Nous ne collectons pas sciemment de données auprès de personnes de moins de 18 ans (et a fortiori de moins de 13 ans). Si nous apprenons qu'un mineur a créé un compte, celui-ci est immédiatement supprimé avec toutes les données associées. Si vous pensez qu'un mineur utilise KoraLove, signalez-le à contact@tubeoptimizer.net.</p>
    <p>L'application ne fait pas partie du programme « Designed for Families » de Google Play et ne cible pas les enfants.</p>

    <H2>8. Modifications de cette politique</H2>
    <p>Nous pouvons mettre à jour cette politique pour refléter des évolutions du service ou de la réglementation. La date de dernière mise à jour est indiquée en haut du document ; en cas de changement important, vous serez informé dans l'application ou par e-mail. La version en vigueur est toujours consultable dans l'application (Profil → Paramètres → Politique de confidentialité) et sur <a href="https://koralove.org/privacy" className="text-kora-terracotta underline">https://koralove.org/privacy</a>.</p>

    <H2>9. Contact</H2>
    <p>Pour toute question relative à la confidentialité ou à vos données personnelles :</p>
    <p><strong>TubeOptimizer LLC</strong> — 30 N Gould St, STE R, Sheridan, WY 82801, États-Unis<br />E-mail : <strong>contact@tubeoptimizer.net</strong></p>
    <p className="text-sm text-kora-muted">TubeOptimizer LLC — 30 N Gould St, STE R, Sheridan, WY 82801, États-Unis — contact@tubeoptimizer.net<br />Cette page est également consultable dans l'application KoraLove (Profil → Paramètres → Politique de confidentialité).</p>
  </PageShell>
);

export const SuppressionCompte = () => (
  <PageShell
    title="Supprimer votre compte KoraLove"
    subtitle="KoraLove — éditée par TubeOptimizer LLC · Demande de suppression de compte et des données associées"
    icon={UserCheck}
    testid="page-suppression-compte"
  >
    <p>Cette page concerne l'application <strong>KoraLove</strong> (Google Play et App Store) et le site <strong>koralove.org</strong>, édités par <strong>TubeOptimizer LLC</strong>. Vous pouvez y demander la suppression définitive de votre compte et des données associées.</p>

    <H2>1. Supprimer votre compte directement dans l'application (recommandé)</H2>
    <div className="not-prose bg-white border border-kora-line rounded-2xl p-5">
      <ol className="list-decimal pl-5 space-y-2 text-sm text-kora-ink/80">
        <li>Ouvrez l'application KoraLove (ou connectez-vous sur koralove.org)</li>
        <li>Allez dans l'onglet <strong>Profil</strong></li>
        <li>Ouvrez <strong>Paramètres → Gestion du compte</strong></li>
        <li>Touchez <strong>« Supprimer mon compte »</strong>, indiquez éventuellement un motif, puis confirmez</li>
      </ol>
      <p className="text-sm text-kora-ink/80 mt-3">La suppression est <strong>immédiate et définitive</strong>. Vous pouvez aussi choisir « Mettre mon compte en pause » si vous souhaitez seulement rendre votre profil invisible temporairement.</p>
    </div>

    <H2>2. Demander la suppression par e-mail</H2>
    <p>Si vous ne pouvez plus accéder à votre compte, envoyez votre demande à <strong>contact@tubeoptimizer.net</strong> <em>depuis l'adresse e-mail liée à votre compte KoraLove</em>, avec pour objet « Suppression de compte KoraLove ». Nous traitons la demande sous <strong>30 jours maximum</strong>.</p>

    <H2>3. Données supprimées et données conservées</H2>
    <p><strong>Données supprimées définitivement</strong> lors de la suppression du compte :</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Votre profil complet (prénom, âge, genre, pays, biographie, centres d'intérêt, préférences)</li>
      <li>Vos photos de profil</li>
      <li>Vos messages, matchs, likes, super likes, visites et visiteurs</li>
      <li>Votre jeton de session et vos jetons de notification push</li>
      <li>Votre adresse e-mail et les identifiants issus de votre compte Google</li>
    </ul>
    <p><strong>Délais :</strong> les données sont effacées de nos systèmes actifs sous <strong>30 jours</strong> et de nos sauvegardes sous <strong>90 jours maximum</strong>.</p>
    <p><strong>Données conservées temporairement</strong> (durée légale applicable, sous forme minimisée) :</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Les données nécessaires à la prévention des fraudes (signalements, comptes bannis)</li>
      <li>Les justificatifs de facturation liés aux achats Premium</li>
      <li>Les journaux techniques du serveur (conservés au maximum 12 mois)</li>
    </ul>

    <H2>4. Pour aller plus loin</H2>
    <p>Consultez notre <a href="/privacy" className="text-kora-terracotta underline">Politique de confidentialité</a> pour le détail complet du traitement de vos données. Vous pouvez aussi retirer l'accès de KoraLove à votre compte Google depuis <a href="https://myaccount.google.com/permissions" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">myaccount.google.com/permissions</a>.</p>
    <p className="text-sm text-kora-muted">TubeOptimizer LLC — 30 N Gould St, STE R, Sheridan, WY 82801, États-Unis — contact@tubeoptimizer.net</p>
  </PageShell>
);

export const SuppressionConversations = () => (
  <PageShell
    title="Supprimer vos conversations et vos matchs"
    subtitle="KoraLove — éditée par TubeOptimizer LLC · Contrôle de vos données de messagerie dans l'application"
    icon={MessageCircle}
    testid="page-suppression-conversations"
  >
    <p>Cette page explique comment supprimer, à tout moment et directement dans l'application <strong>KoraLove</strong> (Google Play, App Store) ou sur <strong>koralove.org</strong>, l'historique de vos conversations et vos matchs. Ces suppressions sont <strong>immédiates, définitives</strong> et effectives <strong>pour les deux membres</strong> de la conversation. Aucune notification n'est envoyée à l'autre membre.</p>

    <H2>1. Vider l'historique d'une conversation (garder le match)</H2>
    <div className="not-prose bg-white border border-kora-line rounded-2xl p-5">
      <ol className="list-decimal pl-5 space-y-2 text-sm text-kora-ink/80">
        <li>Ouvrez l'onglet <strong>Matchs</strong></li>
        <li>Touchez le menu <strong>« ⋯ »</strong> sur la conversation concernée</li>
        <li>Choisissez <strong>« Vider la conversation »</strong>, puis confirmez</li>
      </ol>
      <p className="text-sm text-kora-ink/80 mt-3">Tous les messages de la conversation sont supprimés définitivement de nos serveurs pour les deux membres. Le match est conservé : vous pouvez recommencer à discuter quand vous voulez.</p>
    </div>

    <H2>2. Retirer un match (et sa conversation)</H2>
    <div className="not-prose bg-white border border-kora-line rounded-2xl p-5">
      <ol className="list-decimal pl-5 space-y-2 text-sm text-kora-ink/80">
        <li>Ouvrez l'onglet <strong>Matchs</strong></li>
        <li>Touchez le menu <strong>« ⋯ »</strong> sur la conversation concernée</li>
        <li>Choisissez <strong>« Retirer le match »</strong>, puis confirmez</li>
      </ol>
      <p className="text-sm text-kora-ink/80 mt-3">Le match et l'intégralité de la conversation sont supprimés définitivement pour les deux membres. Les deux profils redeviennent visibles l'un pour l'autre dans Découvrir, comme s'ils ne s'étaient jamais likés.</p>
    </div>

    <H2>3. Tout effacer en une fois</H2>
    <div className="not-prose bg-white border border-kora-line rounded-2xl p-5">
      <ol className="list-decimal pl-5 space-y-2 text-sm text-kora-ink/80">
        <li>Ouvrez l'onglet <strong>Matchs</strong></li>
        <li>Touchez <strong>« Tout effacer »</strong> en haut de la liste</li>
        <li>Choisissez <strong>« Vider toutes les conversations »</strong> (supprime tous les messages, garde vos matchs) ou <strong>« Retirer tous les matchs »</strong> (supprime tous les matchs et toutes les conversations)</li>
        <li>Confirmez l'action</li>
      </ol>
    </div>

    <H2>4. Ce qu'il faut savoir</H2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Les messages supprimés sont effacés de nos systèmes actifs immédiatement et de nos sauvegardes sous <strong>90 jours maximum</strong>.</li>
      <li>Les suppressions concernent <strong>les deux membres</strong> : l'autre personne ne voit plus la conversation ni le match, sans être notifiée.</li>
      <li>Ces actions sont <strong>irréversibles</strong> — une confirmation est demandée avant chaque suppression.</li>
      <li>Aucune conversation ni aucun match supprimé n'est utilisé à d'autres fins ; KoraLove n'exploite pas le contenu de vos messages (voir la <a href="/privacy" className="text-kora-terracotta underline">Politique de confidentialité</a>).</li>
      <li>Pour supprimer l'intégralité de votre compte et de vos données, consultez la page <a href="/supprimer-compte" className="text-kora-terracotta underline">Supprimer votre compte</a>.</li>
    </ul>

    <H2>5. Contact</H2>
    <p>Pour toute question : <strong>contact@tubeoptimizer.net</strong></p>
    <p className="text-sm text-kora-muted">TubeOptimizer LLC — 30 N Gould St, STE R, Sheridan, WY 82801, États-Unis</p>
  </PageShell>
);

export const SecuriteEnfants = () => (
  <PageShell
    title="Normes relatives à la sécurité des enfants"
    subtitle="KoraLove — éditée par TubeOptimizer LLC · Politique contre l'exploitation et les abus sexuels sur mineurs (CSAE)"
    icon={ShieldCheck}
    testid="page-securite-enfants"
  >
    <p>Cette page présente les normes publiées de <strong>KoraLove</strong> (application disponible sur Google Play et l'App Store, et site <strong>koralove.org</strong>) en matière de lutte contre l'exploitation et les abus sexuels sur mineurs (<em>Child Sexual Abuse and Exploitation — CSAE</em>). KoraLove applique une <strong>tolérance zéro</strong> envers toute forme d'exploitation, d'abus ou de mise en danger d'enfants.</p>

    <H2>1. Service strictement réservé aux adultes</H2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>KoraLove est une application de rencontres <strong>exclusivement réservée aux personnes âgées de 18 ans et plus</strong>.</li>
      <li>La date de naissance est obligatoire à l'inscription : tout utilisateur déclarant moins de 18 ans se voit refuser la création de compte.</li>
      <li><strong>Chaque profil est vérifié manuellement</strong> par notre équipe de modération avant d'être approuvé et visible par les autres membres (photos, âge apparent, cohérence des informations).</li>
      <li>Tout compte suspecté d'appartenir à un mineur est immédiatement suspendu, puis supprimé avec l'ensemble des données associées.</li>
    </ul>

    <H2>2. Contenus et comportements strictement interdits</H2>
    <p>Sont interdits sur KoraLove, sans aucune exception :</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Tout contenu d'exploitation ou d'abus sexuels sur mineurs (CSAM), réel, simulé ou généré par intelligence artificielle ;</li>
      <li>Toute sexualisation de mineurs, y compris textes, images, liens ou propos à caractère suggestif impliquant des mineurs ;</li>
      <li>Le grooming : toute tentative de contact, de séduction ou de manipulation d'un mineur à des fins sexuelles ;</li>
      <li>La sollicitation, le partage ou la demande de contenus sexuels impliquant des mineurs ;</li>
      <li>Le trafic, la traite ou toute forme d'exploitation commerciale de mineurs ;</li>
      <li>L'usurpation d'identité visant à se faire passer pour un mineur ou à dissimuler l'âge réel d'un utilisateur.</li>
    </ul>
    <p>Toute violation entraîne la <strong>suppression immédiate et définitive du compte</strong>, la conservation des éléments de preuve, et un <strong>signalement aux autorités compétentes</strong>.</p>

    <H2>3. Signalement dans l'application</H2>
    <p>Chaque membre peut signaler un profil, une photo ou un message à tout moment, directement dans l'application :</p>
    <div className="not-prose bg-white border border-kora-line rounded-2xl p-5">
      <ol className="list-decimal pl-5 space-y-2 text-sm text-kora-ink/80">
        <li>Ouvrez le profil ou la conversation concernée</li>
        <li>Touchez le bouton <strong>« Signaler »</strong></li>
        <li>Sélectionnez le motif (par ex. « Profil suspect / mineur ») et décrivez les faits</li>
      </ol>
      <p className="text-sm text-kora-ink/80 mt-3">Les signalements liés à la sécurité des enfants sont traités <strong>en priorité absolue</strong> par notre équipe de modération. Vous pouvez également bloquer immédiatement le membre concerné.</p>
    </div>

    <H2>4. Traitement des signalements et coopération avec les autorités</H2>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>Tout signalement relatif à un mineur ou à un contenu CSAE est examiné en priorité par un modérateur humain.</li>
      <li>En cas de violation avérée, le compte est banni définitivement et les contenus concernés sont retirés immédiatement.</li>
      <li>Nous signalons les contenus et comportements d'exploitation de mineurs aux <strong>autorités compétentes</strong> (forces de l'ordre et organismes spécialisés tels que le NCMEC ou les points de contact nationaux comme Pharos en France), conformément aux lois applicables.</li>
      <li>Nous coopérons pleinement avec les demandes légales des autorités judiciaires (voir notre page <a href="/demandes-legales" className="text-kora-terracotta underline">Demandes légales</a>).</li>
      <li>Les éléments de preuve sont conservés de manière sécurisée dans les limites prévues par la loi afin d'appuyer les enquêtes.</li>
    </ul>

    <H2>5. Conformité aux lois sur la sécurité des enfants</H2>
    <p>KoraLove se conforme aux lois applicables en matière de lutte contre l'exploitation et les abus sexuels sur mineurs, ainsi qu'au <a href="https://support.google.com/googleplay/android-developer/answer/14747720" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">Règlement de Google Play sur les normes liées à la sécurité des enfants</a> et aux règles de l'App Store d'Apple.</p>

    <H2>6. Point de contact dédié à la sécurité des enfants</H2>
    <p>Pour toute question, tout signalement ou toute demande des autorités concernant la sécurité des enfants et nos normes CSAE, contactez notre point de contact désigné :</p>
    <div className="not-prose bg-white border border-kora-line rounded-2xl p-5">
      <p className="text-sm text-kora-ink/80"><strong>E-mail (sécurité des enfants) :</strong> <a href="mailto:contact@koralove.org" className="text-kora-terracotta font-semibold">contact@koralove.org</a></p>
      <p className="text-sm text-kora-ink/80 mt-2"><strong>Éditeur :</strong> TubeOptimizer LLC — 30 N Gould St, STE R, Sheridan, WY 82801, États-Unis — contact@tubeoptimizer.net</p>
      <p className="text-sm text-kora-ink/80 mt-2">Les messages relatifs à la sécurité des enfants sont traités en priorité, avec un premier retour sous 24 à 48 heures.</p>
    </div>

    <H2>7. Si un enfant est en danger immédiat</H2>
    <p>Si vous pensez qu'un enfant est en danger immédiat, contactez sans attendre les services d'urgence locaux (police ou gendarmerie). Vous pouvez également signaler les contenus d'exploitation de mineurs au <a href="https://report.cybertip.org" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">NCMEC CyberTipline</a> ou, en France, sur la plateforme <a href="https://www.internet-signalement.gouv.fr" className="text-kora-terracotta underline" target="_blank" rel="noreferrer">Pharos</a>.</p>

    <p className="text-sm text-kora-muted">Dernière mise à jour : juin 2026 · TubeOptimizer LLC — 30 N Gould St, STE R, Sheridan, WY 82801, États-Unis</p>
  </PageShell>
);

export const Contact = () => {
  const [form, setForm] = React.useState({ name: "", email: "", message: "" });
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || form.message.trim().length < 10) {
      toast.error("Veuillez remplir tous les champs (message d'au moins 10 caractères).");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "L'envoi a échoué. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell title="Nous contacter" subtitle="Une question, une suggestion, un signalement ? Écrivez-nous." icon={Mail} testid="page-contact">
      <p data-testid="contact-support-email" className="not-prose text-sm text-kora-ink/80 -mt-2 mb-4">
        Notre support est joignable sur <a href="mailto:contact@koralove.org" className="text-kora-terracotta font-semibold">contact@koralove.org</a>
      </p>
      {sent ? (
        <div data-testid="contact-success" className="bg-white rounded-2xl border-2 border-green-500 p-8 text-center not-prose">
          <div className="text-4xl mb-2">💛</div>
          <p className="font-display font-bold text-xl text-kora-ink">Message envoyé !</p>
          <p className="text-sm text-kora-ink/70 mt-2">Notre équipe vous répondra dans les meilleurs délais à l'adresse {form.email}.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="not-prose bg-white rounded-2xl border border-kora-line p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-1.5">Votre nom</label>
            <input
              data-testid="contact-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={80}
              placeholder="Ex. Awa Diallo"
              className="w-full px-4 py-3 rounded-xl border border-kora-line bg-white focus:outline-none focus:border-kora-terracotta"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-1.5">Votre email</label>
            <input
              data-testid="contact-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              maxLength={120}
              placeholder="vous@exemple.com"
              className="w-full px-4 py-3 rounded-xl border border-kora-line bg-white focus:outline-none focus:border-kora-terracotta"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-1.5">Votre message</label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={5}
              maxLength={2000}
              placeholder="Écrivez votre message ici…"
              className="w-full px-4 py-3 rounded-xl border border-kora-line bg-white focus:outline-none focus:border-kora-terracotta resize-none"
            />
            <p className="text-xs text-kora-muted mt-1 text-right">{form.message.length}/2000</p>
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={sending}
            className="w-full bg-kora-terracotta text-white rounded-full py-3.5 font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Mail size={17} /> {sending ? "Envoi en cours…" : "Envoyer le message"}
          </button>
        </form>
      )}
      <p className="text-sm text-kora-ink/60">
        Pour signaler un profil ou un comportement inapproprié, utilisez de préférence les boutons « Signaler » et « Bloquer »
        directement dans l'application : c'est plus rapide et traité en priorité.
      </p>
      <Coordonnees />
    </PageShell>
  );
};

